import { useCallback, useRef, useState } from 'react';

import { imagesApi } from '../api';
import { useToast } from '../context/ToastContext';

/**
 * Upload staging for the composer and the batch dropzone.
 *
 * Files upload as soon as they are chosen, so `imageIds` is ready before the user
 * has finished typing. Local object URLs give an instant preview instead of a
 * spinner over an empty box; they are revoked on removal to avoid leaking.
 */
export function useImageUpload({ max = 8 } = {}) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const previews = useRef(new Map());

  const revoke = useCallback((id) => {
    const url = previews.current.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      previews.current.delete(id);
    }
  }, []);

  const addFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
      if (!files.length) {
        toast.warn('No images found', 'Drop JPG, PNG, BMP or WEBP files.');
        return [];
      }

      const room = max - items.length;
      if (room <= 0) {
        toast.warn('Attachment limit reached', `Up to ${max} images per message.`);
        return [];
      }
      const accepted = files.slice(0, room);
      if (accepted.length < files.length) {
        toast.warn(
          'Some files were skipped',
          `Only ${room} more image${room === 1 ? '' : 's'} fit on this message.`,
        );
      }

      setUploading(true);
      try {
        const result = await imagesApi.upload(accepted);
        const added = (result.images || []).map((image, index) => {
          const url = URL.createObjectURL(accepted[index]);
          previews.current.set(image.id, url);
          return { ...image, previewUrl: url };
        });
        setItems((current) => [...current, ...added]);
        (result.failed || []).forEach((failure) => {
          toast.warn(`${failure.filename} was rejected`, failure.error);
        });
        return added;
      } catch (cause) {
        toast.error(cause, { title: 'Upload failed' });
        return [];
      } finally {
        setUploading(false);
      }
    },
    [items.length, max, toast],
  );

  const remove = useCallback(
    (imageId) => {
      revoke(imageId);
      setItems((current) => current.filter((item) => item.id !== imageId));
    },
    [revoke],
  );

  const clear = useCallback(() => {
    items.forEach((item) => revoke(item.id));
    setItems([]);
  }, [items, revoke]);

  return {
    items,
    imageIds: items.map((item) => item.id),
    uploading,
    isFull: items.length >= max,
    max,
    addFiles,
    remove,
    clear,
  };
}
